<template>
  <v-container class="pa-0 login-wrapper d-flex" fluid>
    <v-row class="mx-0 fill-height">
      <v-col v-if="isMobile" cols="12" sm="6" class="pa-12">
        <img
          :src="formatImageUrl(logoUrl, 800)"
          style="width: 100%; height: auto"
        />
      </v-col>
      <v-col
        v-if="!isMobile"
        cols="12"
        sm="6"
        class="d-flex justify-center align-center"
        style="background: #dfe0d8; height: 100vh"
      >
        <div class="d-flex align-center" style="width: 500px">
          <v-img :src="formatImageUrl(logoUrl, 800)" contain />
        </div>
      </v-col>
      <v-col cols="12" sm="6" class="d-flex align-center">
        <v-row justify="center">
          <v-col cols="11" lg="7" xl="6" class="p-relative" elevation="0">
            <div class="text-center">
              <h2
                class="text-h4 font-weight-bold text-center text-capitalize mb-6"
              >
                {{ isGateCode ? '' : 'Reservation Search' }}
              </h2>
              <div
                v-if="isGateCode && showForm"
                class="text-body-1 font-weight-medium text-center mb-8"
              >
                <div class="text-h4 font-weight-bold">Welcome!</div>
                <br />
                Please enter your reservation confirmation code and the last
                name on your booking below to access your gate code.
              </div>
              <v-form v-if="showForm" @submit.prevent="handleSubmit">
                <v-text-field
                  v-model="confirmationCode"
                  name="confirmationCode"
                  label="Confirmation Code"
                  type="text"
                  outlined
                  dense
                  rounded
                  autocomplete="off"
                  class="rounded-input"
                  @input="error = ''"
                />
                <v-text-field
                  v-model="lastName"
                  name="lastName"
                  label="Last Name"
                  type="text"
                  outlined
                  dense
                  rounded
                  autocomplete="off"
                  class="rounded-input"
                  @input="error = ''"
                />
                <v-btn
                  type="submit"
                  color="#D97853"
                  class="mt-4 submit-button"
                  :loading="loading"
                  block
                  x-large
                  depressed
                  rounded
                >
                  <span class="text-capitalize white--text font-weight-medium">
                    {{ isGateCode ? 'Get Gate Code' : 'Go to Reservation' }}
                  </span>
                </v-btn>
              </v-form>
              <div v-if="error" class="text-caption error--text mt-2">
                {{ error }}
              </div>
              <div v-if="gateCode" class="gate-code-display">
                <div class="gate-code-label mb-3">Your Gate Code</div>
                <div class="gate-code-value mb-3">
                  {{ gateCode }}
                </div>
                <p class="gate-code-instruction mb-6">
                  Please use this code to access the property
                </p>
                <v-btn
                  color="#D97853"
                  class="mt-2 back-button"
                  elevation="2"
                  @click="handleBack"
                  large
                  rounded
                >
                  <span class="white--text font-weight-medium"
                    >Back to Search</span
                  >
                </v-btn>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import deviceMixin from 'components/mixins/deviceMixin'
import CommonFunctions from 'components/mixins/common-functions'
import axios from 'axios'

export default {
  name: 'ConfirmationCodePage',
  mixins: [deviceMixin, CommonFunctions],
  data() {
    return {
      confirmationCode: '',
      lastName: '',
      error: '',
      loading: false,
      logoUrl: '@/assets/images/reception.webp', // Default fallback image
      gateCode: null,
      resetTimer: null,
    }
  },
  computed: {
    isGateCode() {
      return this.$route.query['gate-code'] !== undefined
    },
    showForm() {
      return !this.gateCode
    },
  },
  watch: {
    gateCode(newValue) {
      if (newValue) {
        this.startResetTimer()
      } else {
        this.clearResetTimer()
      }
    },
  },
  beforeDestroy() {
    this.clearResetTimer()
  },
  async mounted() {
    try {
      const { data } = await axios.get('api/tourist/logo_url', {
        params: {
          company: this.$route.query.company,
        },
      })
      if (data.logo) this.logoUrl = data.logo
    } catch (error) {
      console.error('Failed to fetch logo:', error)
    }
  },
  methods: {
    async handleSubmit() {
      if (this.isGateCode) {
        await this.getGateCode()
      } else {
        await this.goToReservation()
      }
    },
    async getGateCode() {
      const invalidCode =
        'The confirmation code or last name does not match our records. Please verify your information and try again.'
      if (!this.confirmationCode) {
        return (this.error = 'Please enter a confirmation code')
      }
      if (!this.lastName) {
        return (this.error =
          'Please enter your last name as it appears on your reservation')
      }
      this.loading = true
      try {
        const { data } = await axios.get('api/tourist/gate-code', {
          params: {
            confirmation_code: this.confirmationCode,
            last_name: this.lastName,
          },
        })
        this.gateCode = data.gate_code
        this.error = ''
      } catch (error) {
        this.error = error.response?.data?.error || invalidCode
        this.gateCode = null
      } finally {
        this.loading = false
      }
    },
    async goToReservation() {
      const invalidCode =
        'The confirmation code or last name does not match our records. Please verify your information and try again.'
      if (!this.confirmationCode) {
        return (this.error = 'Please enter a confirmation code')
      }
      if (!this.lastName) {
        return (this.error =
          'Please enter your last name as it appears on your reservation')
      }
      this.loading = true
      const isValid = await this.$store
        .dispatch('validateUser', {
          id: this.confirmationCode,
          last_name: this.lastName,
        })
        .catch(() => {
          this.error = invalidCode
          this.loading = false
        })
      if (!isValid) return (this.error = invalidCode)
      this.loading = false
      const url = `${window.location.origin}/tourist/trips/${this.confirmationCode}?origin=search`
      window.open(url, '_blank')
    },
    handleBack() {
      this.gateCode = null
      this.clearResetTimer()
      this.confirmationCode = ''
      this.lastName = ''
      this.error = ''
    },
    startResetTimer() {
      this.clearResetTimer()
      this.resetTimer = setTimeout(() => {
        this.handleBack()
      }, 10000)
    },
    clearResetTimer() {
      if (this.resetTimer) {
        clearTimeout(this.resetTimer)
        this.resetTimer = null
      }
    },
  },
}
</script>

<style scoped>
.rounded-input {
  margin-bottom: 8px;
}

.submit-button {
  height: 56px !important;
  text-transform: none !important;
  font-size: 1.2rem !important;
  border-radius: 28px !important;
  font-weight: 700 !important;
}

.gate-code-display {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  padding: 2rem 1.5rem;
  background: linear-gradient(135deg, #f6f8fb 0%, #e9ecef 100%);
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  margin-top: 1rem;
}

.gate-code-label {
  font-size: 1.2rem;
  font-weight: 600;
  color: #4a5568;
  text-transform: uppercase;
  letter-spacing: 1px;
  text-align: center;
}

.gate-code-value {
  font-size: 3.5rem;
  font-weight: 800;
  color: #10b981;
  letter-spacing: 0.5rem;
  padding: 1rem 2rem;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(16, 185, 129, 0.15);
  border: 3px solid #10b981;
  text-align: center;
}

.gate-code-instruction {
  font-size: 1rem;
  color: #6b7280;
  line-height: 1.6;
  text-align: center;
  margin: 0;
}

.back-button {
  min-width: 180px !important;
  text-transform: none !important;
  border-radius: 24px !important;
  padding: 0 32px !important;
}

@media (max-width: 600px) {
  .gate-code-value {
    font-size: 2.5rem;
    letter-spacing: 0.3rem;
    padding: 0.75rem 1.5rem;
  }

  .gate-code-label {
    font-size: 1rem;
  }

  .submit-button {
    font-size: 1rem !important;
  }
}
</style>
